import { Toggle } from "@/components/ui/toggle";
import { cn } from "@/lib/utils";

/**
 * Pencil components `GgRtQ` (active) / `R5Yj9` (default). Rounded pill
 * button with optional count badge. Used in the Bookings page filter row.
 *
 * Backed by `@radix-ui/react-toggle` (via the shadcn `<Toggle>`
 * primitive) — gives us proper `aria-pressed` semantics, keyboard Space
 * to toggle, and consistent focus styling across the app's filter pills.
 * Prop signature is preserved so the 9 call sites compile unchanged.
 */
export function FilterPill({
  active,
  count,
  onClick,
  children,
  className,
}: {
  active: boolean;
  count?: number;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Toggle
      pressed={active}
      onPressedChange={() => onClick?.()}
      // Override the Toggle defaults — we want a full pill (rounded-full)
      // with our brand fill on active, not Toggle's default rectangular
      // bg-transparent → bg-accent.
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border h-auto",
        "data-[state=off]:bg-card data-[state=off]:text-foreground data-[state=off]:border-border data-[state=off]:hover:bg-muted/60",
        "data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary data-[state=on]:hover:bg-primary/90",
        className,
      )}
    >
      <span>{children}</span>
      {typeof count === "number" && (
        <span
          className={cn(
            "rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
            active
              ? "bg-primary-foreground/20 text-primary-foreground"
              : "bg-muted text-muted-foreground",
          )}
        >
          {count}
        </span>
      )}
    </Toggle>
  );
}
